/**
 * Chart of accounts.
 *
 * Group tree on the left, ledgers of the selected group on the right.
 * Groups show their system_key beside the display code, because the code
 * is cosmetic and switchable while the key is what the system reasons
 * about -- worth making visible to whoever maintains the chart.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, Chip, IconButton, Paper, Skeleton, Snackbar, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolderOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/EditOutlined'
import UploadIcon from '@mui/icons-material/UploadFileOutlined'
import { useTranslation } from 'react-i18next'

import { deleteGroup, fetchAccountTree, fetchGroups } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type AccountGroupFlat, type AccountGroupNode } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useLedgers } from '../store/LedgerCache'
import SectionHead from '../components/SectionHead'
import { c } from '../theme'
import LedgerDialog from './LedgerDialog'
import GroupDialog from './GroupDialog'
import ImportDialog from './ImportDialog'
import ScreenTitle from '../components/ScreenTitle'

function flatten(nodes: AccountGroupNode[], depth = 0,
                 out: { n: AccountGroupNode; depth: number }[] = []) {
  for (const n of nodes) { out.push({ n, depth }); flatten(n.children, depth + 1, out) }
  return out
}

export default function ChartOfAccounts() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { refresh } = useLedgers()
  const qc = useQueryClient()
  const nav = useNavigate()

  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [selected, setSelected] = useState<AccountGroupNode | null>(null)
  const [query, setQuery] = useState('')
  const [ledgerDialog, setLedgerDialog] = useState<{ code?: string } | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [groupDialog, setGroupDialog] =
    useState<null | { edit?: AccountGroupFlat; parent?: AccountGroupNode }>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [groupError, setGroupError] = useState<string | null>(null)

  const tree = useQuery({ queryKey: ['account-tree'], queryFn: fetchAccountTree })
  /*
   * The flat list alongside the tree, for the two things the tree cannot
   * answer: which group is the parent (the tree only nests), and whether
   * a group is part of the standard chart or something a user added.
   */
  const groups = useQuery({ queryKey: ['groups'], queryFn: fetchGroups })
  const flatByCode = useMemo(() => {
    const m = new Map<string, AccountGroupFlat>()
    for (const g of groups.data ?? []) m.set(g.code, g)
    return m
  }, [groups.data])

  const rows = useMemo(() => {
    if (!tree.data) return []
    if (query.trim()) {
      const q = query.toLowerCase()
      return flatten(tree.data).filter(({ n }) =>
        n.name_en.toLowerCase().includes(q) || n.code.toLowerCase().includes(q))
    }
    /*
     * Visible = every ancestor expanded.
     *
     * This was a flat pass over the whole list that tracked hidden-ness
     * by DEPTH. With a two-level chart there was nothing wrong with it.
     * At three levels it leaked: a collapsed node marked depth+1 hidden,
     * but its own expanded child then cleared depth+2 on the way past,
     * so grandchildren of a collapsed group appeared on screen attached
     * to nothing. Recursing simply cannot express that state.
     */
    const out: { n: AccountGroupNode; depth: number }[] = []
    const walk = (nodes: AccountGroupNode[], depth: number) => {
      for (const n of nodes) {
        out.push({ n, depth })
        if (expanded.has(n.id)) walk(n.children, depth + 1)
      }
    }
    walk(tree.data, 0)
    return out
  }, [tree.data, expanded, query])

  /*
   * Open the top level once the tree arrives.
   *
   * A chart that can be several levels deep opens showing five roots and
   * nothing else, so finding Sundry Debtors is three clicks before any
   * work starts. The first level is the standard chart's own structure
   * and always worth seeing; below that is the company's own and stays
   * folded.
   */
  const [opened, setOpened] = useState(false)
  useEffect(() => {
    if (opened || !tree.data?.length) return
    setExpanded(new Set(tree.data.map((n) => n.id)))
    setOpened(true)
  }, [tree.data, opened])

  const toggle = (id: number) => setExpanded((prev) => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const afterChange = () => {
    void qc.invalidateQueries({ queryKey: ['account-tree'] })
    void qc.invalidateQueries({ queryKey: ['groups'] })
    // The whole chart is held in memory for type-ahead; a moved account
    // that is not refreshed here keeps answering from its old group.
    void refresh()
  }

  const removeGroup = async (code: string, name: string) => {
    setGroupError(null)
    try {
      await deleteGroup(code)
      if (selected?.code === code) setSelected(null)
      setNotice(t('chart.groupDeleted', { name }))
      afterChange()
    } catch (e) {
      // Refused for a reason worth reading: it still holds accounts, has
      // groups under it, or is part of the standard chart.
      setGroupError(errorMessage(e))
    }
  }

  const editable = can(P.masterEdit)

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <ScreenTitle titleKey="nav.chartOfAccounts" inset={false} sub={t('chart.sub')} />
      </Box>
      <Box sx={{ display: 'grid', gap: 2,
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>

        <Paper variant="outlined">
          <SectionHead label={t('chart.groups')}
            right={editable ? (
              <Button size="small" startIcon={<CreateNewFolderIcon />}
                onClick={() => setGroupDialog({ parent: selected ?? undefined })}>
                {selected ? t('chart.newSubGroup') : t('chart.newGroup')}
              </Button>
            ) : undefined} />
          <Box sx={{ px: 2, pb: 1 }}>
            <TextField size="small" fullWidth placeholder={t('chart.searchGroups')}
              value={query} onChange={(e) => setQuery(e.target.value)} />
          </Box>
          {groupError && (
            <Alert severity="error" sx={{ mx: 2, mb: 1 }}
              onClose={() => setGroupError(null)}>{groupError}</Alert>
          )}
          {tree.isLoading ? (
            <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
          ) : (
            <Box className="ruled" sx={{ maxHeight: 520, overflowY: 'auto' }}>
              {rows.map(({ n, depth }) => (
                <Box key={n.id} onClick={() => setSelected(n)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    px: 2, py: 0.9, cursor: 'pointer',
                    pl: `${16 + depth * 16}px`,
                    bgcolor: selected?.id === n.id ? c.primarySoft : undefined,
                    '&:hover': { bgcolor: c.primarySoft },
                  }}>
                  {n.children.length > 0 ? (
                    <IconButton size="small" tabIndex={-1}
                      aria-label={t(expanded.has(n.id)
                        ? 'chart.collapse' : 'chart.expand',
                        { name: n.name_en })}
                      onClick={(e) => { e.stopPropagation(); toggle(n.id) }}
                      sx={{ p: 0.25 }}>
                      {expanded.has(n.id)
                        ? <ExpandMoreIcon sx={{ fontSize: 17 }} />
                        : <ChevronRightIcon sx={{ fontSize: 17 }} />}
                    </IconButton>
                  ) : <Box sx={{ width: 22 }} />}
                  <Typography sx={{ fontSize: 13.5, flex: 1 }} noWrap>
                    {n.name_en}
                  </Typography>
                  {n.affects_gross_profit && (
                    <Tooltip title={t('chart.grossProfitHint')}>
                      <Box sx={{ width: 6, height: 6, borderRadius: 6,
                        bgcolor: c.warningInk }} />
                    </Tooltip>
                  )}
                  <span className="num" style={{ fontSize: 11.5, color: c.inkMuted,
                    minWidth: 84, textAlign: 'end' }}>{n.code}</span>
                  <Chip size="small" variant="outlined"
                    label={n.ledgers.length} sx={{ height: 18, fontSize: 10 }} />
                  {editable && (
                    <>
                      <Tooltip title={t('chart.editGroup')}>
                        <IconButton size="small" tabIndex={-1} sx={{ p: 0.25 }}
                          aria-label={t('chart.editGroupNamed',
                            { name: n.name_en })}
                          onClick={(e) => {
                            e.stopPropagation()
                            const flat = flatByCode.get(n.code)
                            if (flat) setGroupDialog({ edit: flat })
                          }}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      {/* Offered only where it can succeed: an empty
                          group the user added themselves. A delete button
                          that always refuses teaches people to ignore
                          buttons. */}
                      {!flatByCode.get(n.code)?.is_system
                        && n.ledgers.length === 0
                        && n.children.length === 0 && (
                        <Tooltip title={t('chart.deleteGroup')}>
                          <IconButton size="small" tabIndex={-1} sx={{ p: 0.25 }}
                            aria-label={t('chart.deleteGroupNamed',
                              { name: n.name_en })}
                            onClick={(e) => {
                              e.stopPropagation()
                              void removeGroup(n.code, n.name_en)
                            }}>
                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        <Paper variant="outlined">
          <SectionHead
            label={selected ? selected.name_en : t('chart.ledgers')}
            right={editable ? (
              <Stack direction="row" spacing={1}>
                <Button size="small" startIcon={<UploadIcon />}
                  onClick={() => setImportOpen(true)} sx={{ color: c.inkMuted }}>
                  {t('chart.import')}
                </Button>
                <Button size="small" startIcon={<AddIcon />}
                  disabled={!selected} onClick={() => setLedgerDialog({})}>
                  {t('chart.newLedger')}
                </Button>
              </Stack>
            ) : undefined} />

          {selected && (
            <Typography variant="caption" color="text.secondary"
              sx={{ px: 2, pb: 1, display: 'block' }}>
              {t('chart.key')}: <span className="num">
                {selected.system_key ?? '—'}</span> · {t('chart.code')}:{' '}
              <span className="num">{selected.code}</span>
            </Typography>
          )}

          {!selected ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              {t('chart.pickGroup')}
            </Typography>
          ) : selected.ledgers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              {t('chart.noLedgers')}
            </Typography>
          ) : (
            <Box className="ruled" sx={{ maxHeight: 520, overflowY: 'auto' }}>
              {selected.ledgers.map((l) => (
                <Box key={l.id} sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
                  '&:hover': { bgcolor: c.primarySoft },
                }}>
                  <span className="num" style={{ fontSize: 12, color: c.inkMuted,
                    minWidth: 72, textAlign: 'start' }}>{l.code}</span>
                  <Typography sx={{ fontSize: 13.5, flex: 1, cursor: 'pointer' }}
                    noWrap onClick={() => nav(`/ledger?code=${l.code}`)}>
                    {l.name_en}
                  </Typography>
                  {editable && (
                    <IconButton size="small"
                      aria-label={t('chart.editAccountNamed', { name: l.name_en })}
                      onClick={() => setLedgerDialog({ code: l.code })}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Box>

      {ledgerDialog && (
        <LedgerDialog
          code={ledgerDialog.code}
          groupCode={selected?.code ?? ''}
          groups={groups.data ?? []}
          onClose={() => setLedgerDialog(null)}
          onSaved={afterChange} />
      )}
      {groupDialog && (
        <GroupDialog
          group={groupDialog.edit}
          parent={groupDialog.parent
            ? { code: groupDialog.parent.code,
                name_en: groupDialog.parent.name_en,
                nature: groupDialog.parent.nature }
            : undefined}
          // Same nature only. The server refuses anything else, and an
          // option that cannot be chosen is not worth offering.
          parentOptions={(groups.data ?? []).filter(
            (g) => g.nature === groupDialog.edit?.nature)}
          onClose={() => setGroupDialog(null)}
          onSaved={afterChange} />
      )}
      <Snackbar open={!!notice} autoHideDuration={4000}
        onClose={() => setNotice(null)} message={notice ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
      {importOpen && (
        <ImportDialog onClose={() => setImportOpen(false)} onDone={afterChange} />
      )}
    </>
  )
}
